/* eslint-disable max-len */
import { useField, useFormikContext } from 'formik';
import { ReactElement, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  clearSearchError,
  clearSearchResults,
  selectExitAdjustmentDataEntryMode,
  selectExitAdjustmentFormViewModel,
  selectExitAdjustmentHasSearchMatch,
  setHasSearchMatch,
  setStudentFiles,
} from 'src/redux/slices/exitAdjustmentSlice';
import { ButtonSize } from 'src/types/ButtonSize';
import { MAX_DATE } from 'src/types/Constants';
import { DataEntryMode } from 'src/types/DataEntryMode';
import { DialogType } from 'src/types/DialogTypes';
import { ExitAdjustmentModel } from 'src/types/ExitAdjustmentModel';
import { PanelType } from 'src/types/PanelType';
import { FormDropdownChangeEventConfig } from 'src/types/propTypes/FormDropdownPropTypes';
import { FormTextboxChangeEventConfig } from 'src/types/propTypes/FormTextboxPropTypes';
import { SelectOption } from 'src/types/SelectOption';
import { StudentModel } from 'src/types/StudentModel';
import { getExitAdjustmentValidationSchema } from 'src/types/Validation/ExitAdjustmentValidationSchema';
import { VALIDATION_MESSAGE } from 'src/types/ValidationMessage';
import {
  isDistrictUser,
  isStateAdmin,
  isStateHelpdesk,
} from 'src/utilities/userUtilities';
import { isValidCode } from '../../../services/base.service';
import {
  getExitAdjustmentStudentUpdate,
  getStudentByMSISId,
} from '../../../services/exitAdjustment.service';
import ActionButton from '../../ActionButton/ActionButton';
import FormDateSelector from '../../FormInputs/FormDateSelector/FormDateSelector';
import FormDropdown from '../../FormInputs/FormDropdown/FormDropdown';
import FormTextarea from '../../FormInputs/FormTextarea/FormTextarea';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Modal from '../../Modal/Modal';
import Panel from '../../Panel/Panel';
import ExitAdjustmentUploadDocuments from './ExitAdjustmentUploadDocuments';

const updatedExitTypeOptions: SelectOption[] = [
  {
    value: '01921',
    text: 'Graduated with regular, advanced, International Baccalaureate, or other type of diploma',
  },
  { value: '01922', text: 'Completed school with other credentials' },
  {
    value: '01928',
    text: 'Completed Grade 12, but did not meet all graduation requirements',
  },
  { value: '01927', text: 'Discontinued schooling' },
  {
    value: '73060',
    text: 'Officially withdrew and enrolled in ABE, adult secondary education, or adult ESL program',
  },
  { value: '01924', text: 'Withdrawn due to illness' },
  { value: '01925', text: 'Expelled or involuntarily withdrawn' },
  { value: '01931', text: 'Not enrolled, unknown status' },
  { value: '01926', text: 'Reached maximum age for services' },
  { value: '73068', text: 'Transfer to a private school in the state' },
  {
    value: '73067',
    text: 'Transfer to a school outside of state (includes transfer to a school outside of the country)',
  },
  { value: '01918', text: 'Transferred to home schooling' },
  { value: '73064', text: 'Died' },
];

const ExitAdjustmentPanel = ({
  schoolYear,
}: {
  schoolYear?: string;
  allowEdit?: boolean;
}): ReactElement => {
  const { values, setFieldTouched, setFieldError, setValues } =
    useFormikContext<ExitAdjustmentModel>();
  const dispatch = useAppDispatch();

  const [, msisIdMetaProps] = useField('studentIdentifierState');
  const getIsMsisIDValid = (): boolean => !msisIdMetaProps?.error;

  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [showPendingApprovalModal, setShowPendingApprovalModal] =
    useState(false);
  const hasSearchMatch = useAppSelector(selectExitAdjustmentHasSearchMatch);
  const dataEntryMode = useAppSelector(selectExitAdjustmentDataEntryMode);
  const validationSchema = getExitAdjustmentValidationSchema(dataEntryMode);
  const formViewModel = useAppSelector(selectExitAdjustmentFormViewModel);
  const [isReasonForDenialDisabled, setIsReasonForDenialDisabled] =
    useState(true);
  const [isDenialReasonRequired, setIsDenialReasonRequired] = useState(false);

  let isEditable = values.studentUpdate && !isStateHelpdesk();
  if (isDistrictUser() && values.reviewStatus === 'Approved') {
    isEditable = false;
  }
  const hasMdeApprovalPermissions = isStateAdmin() || isStateHelpdesk();
  const canUpdateReviewStatus =
    hasMdeApprovalPermissions && values.studentUpdate;
  const msisIdTooShort = values.studentIdentifierState.length < 9;

  const searchDisabled =
    msisIdTooShort ||
    !getIsMsisIDValid() ||
    searching ||
    hasSearchMatch ||
    searchMessage !== '' ||
    dataEntryMode === DataEntryMode.EDIT;

  const handleMSISSearch = async (): Promise<void> => {
    const setUserFields = (
      data: StudentModel,
      studentUpdate: boolean
    ): void => {
      setValues(
        (currentValues) => ({
          ...currentValues,
          studentFirstName: data.firstName || '',
          studentLastOrSurname: data.lastName || '',
          studentMiddleName: data.middleName || '',
          birthDate: data.dateOfBirth || '',
          districtName: data.districtName || '',
          leaIdentifier: data.districtId || '',
          schoolId: data.schoolId || '',
          schoolName: data.schoolName || '',
          schoolYear: schoolYear || '',
          cohortEntryDate: data.cohortEntryDate || '',
          lastEnrolledDate: data.lastEnrolledDate || '',
          cohortStatus: data.cohortStatus || '',
          entryType: data.entryType || '',
          enrollmentExitType: data.enrollmentExitType || '',
          enrollmentExitDate: data.enrollmentExitDate || '',
          studentUpdate,
        }),
        true
      );
    };

    setSearching(true);
    dispatch(setHasSearchMatch(false));
    setSearchMessage('');

    const effectiveSchoolYear = schoolYear || values.schoolYear || '';
    const [studentResponse, studentUpdateResponse] = await Promise.all([
      getStudentByMSISId(values.studentIdentifierState, effectiveSchoolYear),
      getExitAdjustmentStudentUpdate(effectiveSchoolYear),
    ]);

    if (isValidCode(studentResponse.status)) {
      setUserFields(
        studentResponse.data,
        isValidCode(studentUpdateResponse.status)
          ? Boolean(studentUpdateResponse.data)
          : false
      );
      dispatch(setHasSearchMatch(true));
    } else if (studentResponse.status == 405) {
      setShowPendingApprovalModal(true);
    } else if (studentResponse.status == 409) {
      setSearchMessage(
        'This student is not enrolled in the selected/assigned LEA and a school you are assigned to. Please check the MSIS ID and try again.'
      );
    } else if (studentResponse.status == 422) {
      // 422 Unprocessable Entity
      setSearchMessage(
        'This student is not enrolled. Please check the MSIS ID and try again.'
      );
    } else {
      // ANY Error Code
      setSearchMessage(
        'MSIS ID Not Found. Please ensure the MSIS ID has been created for the student.'
      );
    }

    setSearching(false);
  };

  const handleMsisValueChange = (
    config: FormTextboxChangeEventConfig
  ): Promise<void> => {
    const currentMsisValue = String(config.value || '');

    const resetMatchedFields = (): void => {
      setValues(
        (currentValues) => ({
          ...currentValues,
          studentIdentifierState: currentMsisValue,
          studentFirstName: '',
          studentLastOrSurname: '',
          studentMiddleName: '',
          birthDate: '',
          districtName: '',
          leaIdentifier: '',
          schoolId: '',
          schoolName: '',
          schoolYear: '',
          cohortEntryDate: '',
          lastEnrolledDate: '',
          cohortStatus: '',
          entryType: '',
          enrollmentExitType: '',
          enrollmentExitDate: '',
          updatedExitType: '',
          removalReason: '',
          updatedExitDate: '',
          schoolStudentTransferredTo: '',
          documentsUploadedToSharePoint: false,
          studentUpdate: false,
          reviewStatus: '',
          denialReason: '',
        }),
        true
      );
      setFieldTouched('studentFirstName', false);
      setFieldTouched('studentLastOrSurname', false);
      setFieldTouched('studentMiddleName', false);
      setFieldTouched('birthDate', false);
      setFieldTouched('districtName', false);
      setFieldTouched('leaIdentifier', false);
      setFieldTouched('schoolId', false);
      setFieldTouched('schoolName', false);
      setFieldTouched('schoolId', false);
      setFieldTouched('schoolYear', false);
      setFieldTouched('cohortEntryDate', false);
      setFieldTouched('lastEnrolledDate', false);
      setFieldTouched('cohortStatus', false);
      setFieldTouched('entryType', false);
      setFieldTouched('enrollmentExitType', false);
      setFieldTouched('enrollmentExitDate', false);
      setFieldTouched('updatedExitType', false);
      setFieldTouched('removalReason', false);
      setFieldTouched('updatedExitDate', false);
      setFieldTouched('schoolStudentTransferredTo', false);
      setFieldTouched('documentsUploadedToSharePoint', false);
      setFieldTouched('reviewStatus', false);
      setFieldTouched('denialReason', false);
      dispatch(setStudentFiles([]));
    };

    setSearchMessage('');
    if (currentMsisValue.length < 9) {
      setFieldError(
        'studentIdentifierState',
        VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9)
      );
    } else {
      setFieldError('studentIdentifierState', undefined);
    }
    dispatch(clearSearchError());
    dispatch(clearSearchResults());

    // Change after match
    if (hasSearchMatch) {
      resetMatchedFields();
      dispatch(setHasSearchMatch(false));
    }

    return Promise.resolve();
  };

  const updateReasonForDenial = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const isDisabled =
      config.value === 'Denied' && canUpdateReviewStatus ? false : true;

    setIsReasonForDenialDisabled(isDisabled);

    if (config.value === 'Denied') {
      setIsDenialReasonRequired(true);
    } else {
      setIsDenialReasonRequired(false);
    }

    /* istanbul ignore next */
    if (config.setValueHook && isDisabled) {
      config.setValueHook('denialReason', '');
    }
  };

  // const formatExitAdjustmentYear = (year: string): string => {
  //   if (/^\d{4}$/.test(year)) {
  //     const prevYear = (parseInt(year, 10) - 1).toString();
  //     return `${prevYear}-${year}`;
  //   }
  //   return year;
  // };

  return (
    <div data-testid="exit-adjustment-input-child">
      <Panel panelType={PanelType.INFO} heading="Student Information">
        <div className="field-row field-row-msis-id">
          <div>
            <FormTextbox
              displayName="MSIS ID"
              field="studentIdentifierState"
              validationSchema={validationSchema}
              disabled={dataEntryMode === DataEntryMode.EDIT}
              onChangeEvent={handleMsisValueChange}
            />
          </div>
          <div className="search-button">
            <ActionButton
              classes="button--secondary"
              size={ButtonSize.SMALL}
              disabled={searchDisabled}
              dataTestId="student-search-button"
              tooltipText="Click to search for student"
              onClick={handleMSISSearch}
              loading={searching}
            >
              Search
            </ActionButton>
          </div>
          <div className="search-message" data-testid="search-error-message">
            {searchMessage}
          </div>
        </div>

        <div className="field-row">
          <FormTextbox
            displayName="Last Name"
            field="studentLastOrSurname"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="First Name"
            field="studentFirstName"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="Middle Name"
            field="studentMiddleName"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormDateSelector
            displayName="Date of Birth"
            field="birthDate"
            validationSchema={validationSchema}
            disabled={true}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
      </Panel>

      <Panel panelType={PanelType.INFO} heading="LEA & School Information">
        <div className="field-row">
          <FormTextbox
            displayName="LEA"
            field="districtName"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="LEA Identifier"
            field="leaIdentifier"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="School"
            field="schoolName"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="School Identifier"
            field="schoolId"
            validationSchema={validationSchema}
            disabled={true}
          />
        </div>
      </Panel>

      <Panel panelType={PanelType.INFO} heading="Exit Adjustment">
        <div className="field-row field-row-exit-adjustment">
          <FormTextbox
            displayName="Entry Type"
            field="entryType"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormDateSelector
            displayName="Entry Date"
            field="lastEnrolledDate"
            validationSchema={validationSchema}
            disabled={true}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
        <div className="field-row field-row-exit-adjustment">
          <FormTextbox
            displayName="Exit Type"
            field="enrollmentExitType"
            validationSchema={validationSchema}
            disabled={true}
          />
          <FormDateSelector
            displayName="Exit Date"
            field="enrollmentExitDate"
            validationSchema={validationSchema}
            disabled={true}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
        <div className="field-row field-row-exit-adjustment">
          <FormDropdown
            displayName="Updated Exit Type"
            field="updatedExitType"
            options={updatedExitTypeOptions}
            isClearable={true}
            disabled={!isEditable}
            validationSchema={validationSchema}
          />
          <FormDateSelector
            displayName="Updated Exit Date"
            field="updatedExitDate"
            validationSchema={validationSchema}
            disabled={!isEditable}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
      </Panel>

      <ExitAdjustmentUploadDocuments
        associatedRecordId={
          dataEntryMode === DataEntryMode.EDIT
            ? values.id
            : formViewModel.newExitAdjustmentId
        }
        schoolYear={schoolYear}
        studentIdentifier={values.studentIdentifierState}
        leaIdentifier={values.leaIdentifier}
        studentUpdate={values.studentUpdate}
        reviewStatus={values.reviewStatus}
        disabled={!isEditable}
      />

      <Panel panelType={PanelType.INFO} heading="MDE Use Only">
        <div className="field-row field-row-mde-review">
          {/* field "reviewStatus" will be disable by default, will be enabled only for users that are authorized to see. Should be handled when implementing permissions. And this is an optional field, otherwise form will be dirty and invalid state, and cannot create any record */}
          <FormDropdown
            displayName="Status (MDE Review)"
            field="reviewStatus"
            isClearable={true}
            disabled={!canUpdateReviewStatus}
            options={formViewModel.statusOptions}
            onChangeEvent={updateReasonForDenial}
            validationSchema={validationSchema}
          />
        </div>
        <div className="field-row">
          <FormTextarea
            displayName="Reason for Denial"
            field="denialReason"
            rows={3}
            validationSchema={validationSchema}
            disabled={!values.studentUpdate || isReasonForDenialDisabled}
            classConfig={{
              labelClasses: isDenialReasonRequired
                ? 'forced-required-label'
                : '',
            }}
          />
        </div>
      </Panel>
      <Modal
        type={DialogType.CONFIRM}
        open={showPendingApprovalModal}
        onClose={() => setShowPendingApprovalModal(false)}
        title="Pending Approval"
      >
        <div>
          <div className="confirm--message">
            You already have a request in Pending Approval status for this
            student. Once the request is Approved or Denied, you may submit
            another request.
          </div>
          <div className="confirm-action-container">
            <ActionButton
              classes="button--secondary"
              onClick={() => setShowPendingApprovalModal(false)}
              dataTestId="pending-approval-ok-button"
            >
              <span>OK</span>
            </ActionButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ExitAdjustmentPanel;
